import { useEffect, useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { subscriptionApi } from '../services/api';
import { Subscription as SubscriptionType, SubscriptionPlan } from '../types';
import { Modal } from '../components/Modal';

const plans = [
  {
    id: 'STARTER',
    name: 'Starter',
    description: 'Perfecto para empezar',
    monthlyPrice: 9.90,
    yearlyPrice: 95.00,
    features: [
      '1 negocio',
      'Hasta 3 servicios',
      'Hasta 50 reservas/mes',
      'Soporte por email',
    ],
    notIncluded: [
      'Analytics',
      'Marketing tools',
      'API access',
    ],
  },
  {
    id: 'PRO',
    name: 'Pro',
    description: 'Para negocios en crecimiento',
    monthlyPrice: 19.90,
    yearlyPrice: 190.00,
    features: [
      '1 negocio',
      'Servicios ilimitados',
      'Reservas ilimitadas',
      'Hasta 5 empleados',
      'Analytics básico',
      'Soporte prioritario',
    ],
    notIncluded: [
      'Marketing tools',
      'API access',
    ],
    popular: true,
  },
  {
    id: 'BUSINESS',
    name: 'Business',
    description: 'Para equipos profesionales',
    monthlyPrice: 39.90,
    yearlyPrice: 380.00,
    features: [
      'Múltiples ubicaciones',
      'Todo ilimitado',
      'Empleados ilimitados',
      'Analytics avanzado',
      'Marketing tools',
      'API access',
      'Soporte 24/7',
    ],
    notIncluded: [],
  },
];

export function Subscription() {
  const [currentSubscription, setCurrentSubscription] = useState<SubscriptionType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await subscriptionApi.getCurrentSubscription();
      setCurrentSubscription(response.data.data.subscription);
    } catch (error) {
      // No subscription is OK
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    setIsCheckoutLoading(true);
    try {
      const response = await subscriptionApi.createCheckout({
        plan: planId,
        interval: isYearly ? 'yearly' : 'monthly',
        successUrl: `${window.location.origin}/subscription?success=true`,
        cancelUrl: `${window.location.origin}/subscription?canceled=true`,
      });

      window.location.href = response.data.data.checkoutUrl;
    } catch (error) {
      toast.error('Failed to start checkout');
      setIsCheckoutLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) return;

    try {
      await subscriptionApi.cancelSubscription();
      toast.success('Subscription cancelled successfully');
      fetchSubscription();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const handleReactivate = async () => {
    try {
      await subscriptionApi.reactivateSubscription();
      toast.success('Subscription reactivated successfully');
      fetchSubscription();
    } catch (error) {
      toast.error('Failed to reactivate subscription');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Current Subscription */}
      {currentSubscription && (
        <div className="card p-6">
          <h2 className="text-xl font-bold text-secondary-900 mb-4">Current Subscription</h2>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-lg font-semibold text-primary-600">
                {currentSubscription.plan} Plan
              </p>
              <p className="text-sm text-secondary-500">
                {currentSubscription.interval === 'monthly' ? 'Monthly' : 'Yearly'} billing
              </p>
              <p className="text-sm text-secondary-500">
                Status:{' '}
                <span
                  className={`font-medium ${
                    currentSubscription.status === 'ACTIVE'
                      ? 'text-green-600'
                      : 'text-yellow-600'
                  }`}
                >
                  {currentSubscription.status}
                </span>
              </p>
              {currentSubscription.cancelAtPeriodEnd && (
                <p className="text-sm text-red-600 mt-1">
                  Your subscription will end on{' '}
                  {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              {currentSubscription.status === 'ACTIVE' && !currentSubscription.cancelAtPeriodEnd ? (
                <button onClick={handleCancel} className="btn-danger">
                  Cancel Subscription
                </button>
              ) : (
                <button onClick={handleReactivate} className="btn-primary">
                  Reactivate
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pricing Plans */}
      <div>
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-secondary-900">Choose Your Plan</h1>
          <p className="mt-2 text-secondary-500">
            Select the perfect plan for your fitness business
          </p>

          {/* Billing Toggle */}
          <div className="mt-6 flex items-center justify-center space-x-4">
            <span className={`text-sm ${!isYearly ? 'font-medium text-secondary-900' : 'text-secondary-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isYearly ? 'bg-primary-600' : 'bg-secondary-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isYearly ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm ${isYearly ? 'font-medium text-secondary-900' : 'text-secondary-500'}`}>
              Yearly
              <span className="ml-1 text-xs text-green-600">(Save up to 20%)</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`card p-6 relative ${
                plan.popular ? 'ring-2 ring-primary-500' : ''
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                  Most Popular
                </span>
              )}

              <div className="text-center">
                <h3 className="text-xl font-bold text-secondary-900">{plan.name}</h3>
                <p className="text-sm text-secondary-500 mt-1">{plan.description}</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-secondary-900">
                    €{isYearly ? plan.yearlyPrice : plan.monthlyPrice}
                  </span>
                  <span className="text-secondary-500">/{isYearly ? 'year' : 'month'}</span>
                </div>
              </div>

              <ul className="mt-6 space-y-3">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start">
                    <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                    <span className="text-sm text-secondary-600">{feature}</span>
                  </li>
                ))}
                {plan.notIncluded.map((feature, idx) => (
                  <li key={idx} className="flex items-start opacity-50">
                    <XMarkIcon className="h-5 w-5 text-secondary-400 mr-2 flex-shrink-0" />
                    <span className="text-sm text-secondary-500">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={isCheckoutLoading || currentSubscription?.plan === plan.id}
                className={`w-full mt-6 py-3 px-4 rounded-lg font-medium transition-colors ${
                  currentSubscription?.plan === plan.id
                    ? 'bg-green-100 text-green-700 cursor-default'
                    : plan.popular
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                }`}
              >
                {currentSubscription?.plan === plan.id
                  ? 'Current Plan'
                  : isCheckoutLoading
                  ? 'Loading...'
                  : 'Subscribe'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
